// modules
import { CallType } from '@weblite/ts-action/media-proxy'
import { addModal, closeCurrentModal } from 'weblite-web-modal-viewer'
import { t } from '$i18n'
import { getUser, getUserDisplayName } from '$logic/users'
import { toLocale } from '$func/text'
import { changeHallOwner, getHall, removeHall } from '$logic/hall'
import { addMembers, leaveHall, removeMembers } from '$logic/subscription'
import {
  closeChannelAdminPermissions,
  initializeMainApp,
  openDeleteAccount,
} from '$logic/router'
import { logOutUser } from '$logic/auth'
import { deleteRoomByRoomId, getRoom, getRoomDisplayName } from '$logic/room'
import { clearPvHistory, deletePv, getPv, getPvByUserId } from '$logic/pv'
import { deleteContact } from '$logic/contacts/service'
import { deleteMessages } from '$logic/message'
import { deleteAllRecentSearch } from '$logic/room-list/room-list-search.service'
import {
  removeFromOpenedUserList,
  resetOpenedUserList,
} from '$logic/opened-user-list'
import { synchronizePersist } from '$func/webApis'
import { inviteViaPhoneNumber } from '$logic/invite/service'
import { nothing } from '$func/utils'
import { toHumanReadablePhone } from '$func/phone'
import { showChatsRemovedSnackbar } from '$comp/snackbars'
import { ReportIcon } from '$common/icons/report-problem'
import { inviteApi } from '$comp/_app/components/leftPanel/components/invite-friends'
import {
  clearWappRoomHistory,
  deleteWappRoom,
  getWappRoomByWappId,
} from '$logic/wapp-room'
import { getWapp } from '$logic/wapps/service'
import { boldSome } from './confirmation-dialog.helper'
import { ConfirmationDialog } from './confirmation-dialog'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { canRoomHaveCourseVitrine } from '$logic/hall/react-hook'
import { deleteWorkspace, moveHallsToWorkspace } from '$logic/workspace'

const COMPONENT_ID = 'confirmDialog'

const showConfirmModal = (props: Parameters<typeof ConfirmationDialog>[0]) =>
  addModal({ id: COMPONENT_ID, props })

export const showDeleteHallModal = async (hallId: string) => {
  const { type } = (await getRoom(hallId)) || {}
  const persianHallType =
    type === 'GROUP' ? t('common.group') : t('common.channel')
  showConfirmModal({
    description: t('confirmDialog.deleteHall.description')(persianHallType),
    checkboxOption: t('confirmDialog.deleteHall.checkboxOption')(
      persianHallType,
    ),
    title: t('confirmDialog.deleteHall.title')(persianHallType),
    cancelOption: t('confirmDialog.deleteHall.cancelOption'),
    confirmOption: t('confirmDialog.deleteHall.confirmOption')(persianHallType),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async (checked = false) => {
      await closeCurrentModal()
      // TODO: Use a method to close pages that contain information about this hall
      await initializeMainApp()
      // TODO: Show Loading
      await removeHall(hallId, checked)
    },
  })
}

export const showLogoutModal = () =>
  showConfirmModal({
    description: t('confirmDialog.logout.description'),
    title: t('confirmDialog.logout.title'),
    cancelOption: t('confirmDialog.logout.cancelOption'),
    confirmOption: t('confirmDialog.logout.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: logOutUser,
  })

export const showClearDbModal = () =>
  showConfirmModal({
    description: t('confirmDialog.clearDb.description'),
    title: t('confirmDialog.clearDb.title'),
    cancelOption: t('common.cancel'),
    confirmOption: t('common.ok'),
    alertMode: false,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      analite('PROFILE_SETTING_CLEAR_STORAGE')
      synchronizePersist()
    },
  })

export const showDeleteAccountModal = () =>
  showConfirmModal({
    description: t('confirmDialog.deleteAccount.description'),
    title: t('confirmDialog.deleteAccount.title'),
    confirmOption: t('confirmDialog.deleteAccount.confirmOption'),
    cancelOption: t('common.cancel'),
    alertMode: true,
    onCancel: closeCurrentModal,
    async onConfirm() {
      await closeCurrentModal()

      void openDeleteAccount()
    },
  })

export const showTerminateSessionsModal = (onConfirm: () => unknown) =>
  showConfirmModal({
    description: t('confirmDialog.terminateSessions.description'),
    title: t('confirmDialog.terminateSessions.title'),
    cancelOption: t('confirmDialog.terminateSessions.cancelOption'),
    confirmOption: t('confirmDialog.terminateSessions.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      onConfirm()
    },
  })

export const showLeaveHallModal = async (hallId: string) => {
  const { type } = await getRoom(hallId)
  const { name } = await getHall(hallId)

  const persianHallType =
    type === 'GROUP' ? t('common.group') : t('common.channel')

  const description = boldSome(
    t('confirmDialog.leaveHall.description.prefix'),
    name,
    t('confirmDialog.leaveHall.description.postfix'),
  )

  return showConfirmModal({
    description,
    title: t('confirmDialog.leaveHall.title')(persianHallType),
    cancelOption: t('confirmDialog.leaveHall.cancelOption'),
    confirmOption: t('confirmDialog.leaveHall.confirmOption')(persianHallType),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      // TODO: Show Loading
      // TODO: Use a method to close pages that contain information about this hall
      await initializeMainApp()
      await leaveHall(hallId)
    },
  })
}

export const showDeleteContactModal = (userId: string) =>
  showConfirmModal({
    description: t('confirmDialog.deleteContact.description'),
    title: t('confirmDialog.deleteContact.title'),
    cancelOption: t('confirmDialog.deleteContact.cancelOption'),
    confirmOption: t('confirmDialog.deleteContact.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      //  TODO: Show loading
      await deleteContact(userId)
    },
  })

export const showDeleteMessageModal = async (
  roomId: string,
  iids: number[],
) => {
  const room = await getRoom(roomId)
  let description = ''
  let descriptionText = ''
  if (room.type === 'PV') {
    const pv = await getPv(roomId)
    const user = await getUser(pv.to)
    const username = getUserDisplayName(user)
    descriptionText = t('confirmDialog.deleteMessage.description.forPv')(
      username,
    )
  } else if (room.type === 'GROUP') {
    descriptionText = t('confirmDialog.deleteMessage.description.forGroup')
  }

  description =
    iids.length === 1
      ? t('confirmDialog.deleteMessage.description.oneMessage')(descriptionText)
      : t('confirmDialog.deleteMessage.description.manyMessage')(
          descriptionText,
        )

  return showConfirmModal({
    description,
    title:
      iids.length === 1
        ? t('confirmDialog.deleteMessage.title.oneMessage')
        : t('confirmDialog.deleteMessage.title.manyMessage')(
            toLocale(iids.length),
          ),
    cancelOption: t('confirmDialog.deleteMessage.cancelOption'),
    confirmOption: t('confirmDialog.deleteMessage.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      // TODO: Show loading modal
      analite('CHAT_MESSAGE_DELETE')
      await deleteMessages(roomId, iids)
    },
  })
}

export const showClearPVHistory = async (userId: string) => {
  const user = await getUser(userId)
  const fullName = getUserDisplayName(user)

  const description = boldSome(
    t('confirmDialog.clearPVHistory.description.prefix'),
    fullName,
    t('confirmDialog.clearPVHistory.description.postfix'),
  )

  showConfirmModal({
    description,
    title: t('confirmDialog.clearPVHistory.title'),
    cancelOption: t('confirmDialog.clearPVHistory.cancelOption'),
    confirmOption: t('confirmDialog.clearPVHistory.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      analite('CHATLIST_CLEAR_HISTORY')
      // TODO: Show loading modal
      const pv = await getPvByUserId(userId)
      await clearPvHistory(pv.roomId)
    },
  })
}

export const showClearWappRoomHistory = async (wappId: string) => {
  const wappName = (await getWapp(wappId))?.name
  const roomId = (await getWappRoomByWappId(wappId))?.roomId
  const description = boldSome(
    t('confirmDialog.clearPVHistory.description.prefix'),
    wappName,
    t('confirmDialog.clearPVHistory.description.postfix'),
  )
  if (!wappName || !roomId) return

  showConfirmModal({
    description,
    title: t('confirmDialog.clearPVHistory.title'),
    cancelOption: t('confirmDialog.clearPVHistory.cancelOption'),
    confirmOption: t('confirmDialog.clearPVHistory.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      analite('WAPP_ROOM_CLEAR_HISTORY')
      // TODO: Show loading modal
      await clearWappRoomHistory(roomId)
    },
  })
}

export const showClearAllPVsHistory = async (roomIds: string[]) => {
  const descriptionText =
    roomIds.length < 3
      ? await Promise.all(
          roomIds.map(async roomId => {
            const pv = await getPv(roomId)
            const user = await getUser(pv.to)
            return getUserDisplayName(user)
          }),
        ).then(names => names.join(t('common.and')))
      : t('confirmDialog.clearPVHistory.moreThan3')(toLocale(roomIds.length))

  const description = boldSome(
    t('confirmDialog.clearPVHistory.description.prefix'),
    descriptionText,
    t('confirmDialog.clearPVHistory.description.postfix'),
  )

  showConfirmModal({
    description,
    title: t('confirmDialog.clearPVHistory.title'),
    cancelOption: t('confirmDialog.clearPVHistory.cancelOption'),
    confirmOption: t('confirmDialog.clearPVHistory.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      // TODO: Show loading modal
      analite('CHATLIST_CLEAR_HISTORY')
      await Promise.all(roomIds.map(clearPvHistory))
    },
  })
}

export const showBanUserConfirmModal = async (
  roomId: string,
  userId: string,
) => {
  const user = await getUser(userId)
  const fullName = getUserDisplayName(user)

  const description = boldSome(
    t('confirmDialog.banUser.description.prefix'),
    fullName,
    t('confirmDialog.banUser.description.postfix'),
  )
  return showConfirmModal({
    description,
    title: t('confirmDialog.banUser.title'),
    confirmOption: t('confirmDialog.banUser.confirmOption'),
    cancelOption: t('confirmDialog.banUser.cancelOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      // TODO: Show loading
      const room = await getRoom(roomId)
      analite('CHAT_REMOVE_MEMBER', {
        type: room?.type,
        source: 'REMOVE_MEMBERS',
      })
      await removeMembers(roomId, [userId])
      removeFromOpenedUserList(userId)
      await closeCurrentModal()
    },
  })
}

export const showDeleteRoomsModal = async (roomIds: string[]) => {
  showConfirmModal({
    description: t('confirmDialog.deleteChat.description'),
    title: t('confirmDialog.deleteChat.title')(roomIds.length),
    cancelOption: t('confirmDialog.deletePv.cancelOption'),
    confirmOption: t('confirmDialog.deleteChat.confirmOption'),
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      await initializeMainApp()
      await Promise.all(roomIds.map(deleteRoomByRoomId))
      showChatsRemovedSnackbar()
    },
  })
}

export const showDeleteWappRoomModal = async (wappId: string) => {
  const wappName = (await getWapp(wappId))?.name
  const roomId = (await getWappRoomByWappId(wappId))?.roomId

  if (!roomId) return
  const description = boldSome(
    t('confirmDialog.deletePv.description.prefix'),
    wappName,
    t('confirmDialog.deletePv.description.postfix'),
  )
  showConfirmModal({
    description,
    title: t('confirmDialog.deletePv.title'),
    cancelOption: t('confirmDialog.deletePv.cancelOption'),
    confirmOption: t('confirmDialog.deletePv.confirmOption'),
    // checkboxOption: t('confirmDialog.deletePv.checkboxOption'),
    // TODO: Temporary avoid checking for clearing history because it has bugs
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async checked => {
      await closeCurrentModal()
      //   TODO: Show loading
      // TODO: Use a method to close pages that contain information about this pv
      await initializeMainApp()
      await deleteWappRoom(roomId, !!checked)
    },
  })
}

export const showDeletePvModal = async (userId: string) => {
  const user = await getUser(userId)
  const description = boldSome(
    t('confirmDialog.deletePv.description.prefix'),
    getUserDisplayName(user),
    t('confirmDialog.deletePv.description.postfix'),
  )
  showConfirmModal({
    description,
    title: t('confirmDialog.deletePv.title'),
    cancelOption: t('confirmDialog.deletePv.cancelOption'),
    confirmOption: t('confirmDialog.deletePv.confirmOption'),
    // checkboxOption: t('confirmDialog.deletePv.checkboxOption'),
    // TODO: Temporary avoid checking for clearing history because it has bugs
    alertMode: true,
    onCancel: closeCurrentModal,
    onConfirm: async checked => {
      await closeCurrentModal()
      //   TODO: Show loading
      // TODO: Use a method to close pages that contain information about this pv
      await initializeMainApp()
      const pv = await getPvByUserId(userId)
      await deletePv(pv.roomId, checked)
    },
  })
}

export const showDiscardChangesConfirmModal = (
  onDiscard: () => unknown,
  onApply: () => unknown,
) =>
  showConfirmModal({
    title: t('confirmDialog.discardChanges.title'),
    description: t('confirmDialog.discardChanges.description'),
    confirmOption: t('confirmDialog.discardChanges.confirmOption'),
    cancelOption: t('confirmDialog.discardChanges.cancelOption'),
    onCancel: async () => {
      await closeCurrentModal()
      onDiscard()
    },
    onConfirm: async () => {
      await closeCurrentModal()
      onApply()
    },
  })

export const showAddMemberModal = async (
  hallId: string,
  userIds: string[],
  onComplete: () => unknown,
) => {
  const newMembers =
    userIds.length < 3
      ? await Promise.all(
          userIds.map(async userId => {
            const user = await getUser(userId)
            return getUserDisplayName(user)
          }),
        ).then(names => names.join(t('common.and')))
      : t('confirmDialog.addMember.newMembersGreaterThan3')(
          toLocale(userIds.length),
        )

  // NOTE: if there is just one member no need to stipulate number
  const title = t('confirmDialog.addMember.title')(
    toLocale(newMembers.length > 1 ? `${userIds.length} ` : ''),
  )

  const { name } = await getHall(hallId)

  const description = boldSome(
    t('confirmDialog.addMember.description.prefix'),
    newMembers,
    boldSome(
      t('confirmDialog.addMember.description.postfix.prefix'),
      name,
      t('confirmDialog.addMember.description.postfix.postfix'),
    ),
  )

  return showConfirmModal({
    title,
    description,
    confirmOption: t('confirmDialog.addMember.confirmOption'),
    cancelOption: t('confirmDialog.addMember.cancelOption'),
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()

      showSaveChangesLoadingModal()
      await addMembers(hallId, userIds)
      await closeCurrentModal()

      onComplete()
    },
  })
}

export const showDeleteSearchHistoryConfirmModal = () =>
  showConfirmModal({
    description: t('confirmDialog.clearSearchHistory.description'),
    title: t('confirmDialog.clearSearchHistory.title'),
    confirmOption: t('confirmDialog.clearSearchHistory.confirmOption'),
    cancelOption: t('confirmDialog.clearSearchHistory.cancelOption'),
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      await closeCurrentModal()
      await deleteAllRecentSearch()
    },
  })

export const showOpenLinkConfirmModal = (
  link: string,
  onConfirm: () => unknown,
) =>
  showConfirmModal({
    description: link,
    title: t('confirmDialog.openLink.title'),
    confirmOption: t('common.ok'),
    cancelOption: t('common.cancel'),
    onCancel: closeCurrentModal,
    onConfirm: async () => {
      onConfirm()
      await closeCurrentModal()
    },
  })

export const showInviteConfirmModal = async (
  phoneNumbers: string[],
  cb = nothing,
) =>
  showConfirmModal({
    description:
      phoneNumbers.length === 1
        ? t('confirmDialog.invite.description')(
            toHumanReadablePhone(phoneNumbers[0]),
          )
        : t('confirmDialog.invite.descriptions')(phoneNumbers.length),
    title: t('confirmDialog.invite.title'),
    confirmOption: t('common.ok'),
    cancelOption: t('common.cancel'),
    onCancel: async () => {
      // TODO:
      await closeCurrentModal()
    },
    onConfirm: async () => {
      await inviteViaPhoneNumber(phoneNumbers, true)
      inviteApi.updateInviteList()
      await closeCurrentModal()
      cb()
    },
  })

export const showDeleteContentConfirmModal = async (cb = nothing) =>
  showConfirmModal({
    description: t('confirmDialog.deleteContent.description'),
    title: t('bookmarkModal.remove'),
    confirmOption: t('common.ok'),
    cancelOption: t('common.cancel'),
    onCancel: async () => {
      // TODO:
      await closeCurrentModal()
    },
    onConfirm: async () => {
      await closeCurrentModal()
      cb()
    },
  })

export const showDeleteContentFromInlineConfirmModal = async (
  roomId: string,
  cb: (checked: boolean) => unknown,
) => {
  const hasCourseVitrine = await canRoomHaveCourseVitrine(roomId)
  return showConfirmModal({
    description: t('confirmDialog.deleteContent.description'),
    title: t('bookmarkModal.remove'),
    confirmOption: t('common.ok'),
    cancelOption: t('common.cancel'),
    checkboxOption: hasCourseVitrine
      ? t('confirmDialog.deleteContentFromInline.checkboxOption')
      : '',
    onCancel: async () => {
      // TODO:
      await closeCurrentModal()
    },
    onConfirm: async checked => {
      await closeCurrentModal()
      cb(hasCourseVitrine ? !!checked : false)
    },
  })
}

export const showDiscardRecordingVoiceConfirmModal = async (
  isRecording: boolean,
) =>
  new Promise<boolean>(resolve => {
    showConfirmModal({
      title: t(
        `confirmDialog.${isRecording ? 'discardRecordingVoice' : 'discardVoiceMessage'}.title`,
      ),
      description: t(
        `confirmDialog.${isRecording ? 'discardRecordingVoice' : 'discardVoiceMessage'}.description`,
      ),
      confirmOption: t('common.remove'),
      cancelOption: t('common.cancel'),
      alertMode: true,
      onCancel: async () => {
        // TODO:
        await closeCurrentModal()
        resolve(false)
      },
      onConfirm: async () => {
        await closeCurrentModal()
        resolve(true)
      },
    })
  })

export const showDiscardForwardConfirmModal = async (
  sourceRoomId: string,
  numOfMessages: number,
  onConfirm: () => unknown,
) => {
  const sourceName = await getRoomDisplayName(sourceRoomId)
  showConfirmModal({
    title: t('forward.nMessages')(numOfMessages),
    description: t('forward.whatUDo')(numOfMessages, sourceName),
    confirmOption: t('forward.discardForward'),
    cancelOption: t('common.resume'),
    alertMode: true,
    onCancel: async () => {
      await closeCurrentModal()
    },
    onConfirm: async () => {
      onConfirm()
      await closeCurrentModal()
    },
  })
}

export const showPinMessageConfirmModal = async (
  destRoomId: string,
  onConfirm: (selected?: boolean) => unknown,
) => {
  const room = await getRoom(destRoomId)
  if (!room) return
  const checkboxOption =
    room.type === 'CHANNEL' || room.type === 'GROUP'
      ? t('confirmDialog.pinMessage.sendNotif')(room.type === 'GROUP')
      : room.type === 'PV' || room.type === 'WAPP_ROOM'
        ? t('confirmDialog.pinMessage.pinForUser')(
            await getRoomDisplayName(destRoomId),
          )
        : null

  showConfirmModal({
    title: t('confirmDialog.pinMessage.title'),
    description: t('confirmDialog.pinMessage.description'),
    confirmOption: t('chatList.menu.pin'),
    cancelOption: t('common.cancel'),
    checkboxOption,
    onCancel: async () => {
      await closeCurrentModal()
    },
    onConfirm: async (checked?: boolean) => {
      onConfirm(checked)
      await closeCurrentModal()
    },
  })
}

export const showUnPinMessageConfirmModal = async (
  onConfirm: () => unknown,
) => {
  showConfirmModal({
    title: t('confirmDialog.unPinMessage.title'),
    description: t('confirmDialog.unPinMessage.description'),
    confirmOption: t('chatList.menu.unPin'),
    cancelOption: t('common.cancel'),
    onCancel: async () => {
      await closeCurrentModal()
    },
    onConfirm: async () => {
      onConfirm()
      await closeCurrentModal()
    },
  })
}
export const showPromoteToOwnerConfirmModal = async (
  hallId: string,
  userId: string,
) => {
  const user = await getUser(userId)
  showConfirmModal({
    title: t('confirmDialog.promoteToOwner.title'),
    description: t('confirmDialog.promoteToOwner.description')(
      getUserDisplayName(user),
    ),
    confirmOption: t('common.ok'),
    cancelOption: t('common.cancel'),
    onCancel: async () => {
      await closeCurrentModal()
    },
    onConfirm: async () => {
      await closeCurrentModal()
      await changeHallOwner(hallId, userId)

      resetOpenedUserList()
      resetOpenedUserList('admins')
      await closeChannelAdminPermissions()
    },
  })
}

export const showStartScheduledCallDialog = async (callType: CallType) => {
  const type = t(`call.${callType}`)
  const title = t(`confirmDialog.startScheduledCall.title`)(type)
  const description = t(`confirmDialog.startScheduledCall.description`)(type)
  const confirmOption = t(`confirmDialog.startScheduledCall.confirmOption`)
  const cancelOption = t(`confirmDialog.startScheduledCall.cancelOption`)

  return new Promise((resolve, reject) => {
    showConfirmModal({
      title,
      description,
      confirmOption,
      cancelOption,
      onCancel: async () => {
        await closeCurrentModal()
        reject()
      },
      onConfirm: async () => {
        await closeCurrentModal()
        resolve(null)
      },
    })
  })
}

export const showDeleteScheduledCallConfirmationModal = async (
  callType: CallType,
) => {
  const type = t(`call.${callType === 'classroom' ? 'class' : callType}`)
  const title = t(`confirmDialog.deleteScheduledCall.title`)(type)
  const description = t(`confirmDialog.deleteScheduledCall.description`)(type)
  const confirmOption = t(`confirmDialog.deleteScheduledCall.confirmOption`)
  const cancelOption = t(`confirmDialog.deleteScheduledCall.cancelOption`)

  return new Promise((resolve, reject) => {
    showConfirmModal({
      title,
      description,
      confirmOption,
      cancelOption,
      alertMode: true,
      onCancel: async () => {
        await closeCurrentModal()
        reject()
      },
      onConfirm: async () => {
        await closeCurrentModal()
        resolve(null)
      },
    })
  })
}

export const showEndOngoingCallConfirmModal = async ({
  ongoingCallType,
  ongoingCallTitle,
  newCallType,
  newCallTitle,
  onConfirm,
}: {
  ongoingCallType: CallType
  ongoingCallTitle: string
  newCallType: CallType
  newCallTitle: string
  onConfirm: () => unknown
}) => {
  const ongoingCallPrefix = t(
    `confirmDialog.endOngoingCall.callTypes.${ongoingCallType}`,
  )
  const newCallPrefix = t(
    `confirmDialog.endOngoingCall.callTypes.${newCallType}`,
  )

  return new Promise((resolve, reject) => {
    showConfirmModal({
      title: t('confirmDialog.endOngoingCall.title'),
      description: t('confirmDialog.endOngoingCall.description')(
        `${ongoingCallPrefix} ${ongoingCallTitle}`,
        `${newCallPrefix} ${newCallTitle}`,
      ),
      confirmOption: t('confirmDialog.endOngoingCall.confirmOption'),
      cancelOption: t('common.cancel'),
      onCancel: async () => {
        await closeCurrentModal()
        reject()
      },
      onConfirm: async () => {
        await onConfirm()
        await closeCurrentModal()
        resolve(null)
      },
    })
  })
}

export const showDeleteStoryConfirmationDialog = async (
  onConfirm: () => unknown,
) => {
  showConfirmModal({
    title: t('story.deleteStoryModal.title'),
    description: t('story.deleteStoryModal.description'),
    confirmOption: t('common.remove'),
    cancelOption: t('common.cancel'),
    alertMode: true,
    onConfirm: async () => {
      await closeCurrentModal()
      onConfirm()
    },
    onCancel: async () => {
      await closeCurrentModal()
    },
  })
}

export const showDeleteSyncedContactsConfirmationDialog = async (
  onConfirm: () => unknown,
) => {
  showConfirmModal({
    title: t('settings.sections.contacts.deleteSyncContacts'),
    description: t('confirmDialog.deleteSyncedContacts.description')(),
    confirmOption: t('common.remove'),
    cancelOption: t('common.cancel'),
    alertMode: true,
    onConfirm: async () => {
      await closeCurrentModal()
      onConfirm()
    },
    onCancel: async () => {
      await closeCurrentModal()
    },
  })
}
export const showCallIsNotAvailableInIOSConfirmModal = async () =>
  showConfirmModal({
    icon: <ReportIcon />,
    description: t('confirmDialog.callInIos.description'),
    confirmOption: t('common.understand'),
    onConfirm: async () => {
      await closeCurrentModal()
    },
  })

export const showDisableRecentUsersModal = async (
  onConfirm: () => Promise<unknown>,
) =>
  showConfirmModal({
    title: t('confirmDialog.disableRecentUsers.title'),
    description: t('confirmDialog.disableRecentUsers.description'),
    confirmOption: t('common.deactive'),
    cancelOption: t('common.cancel'),
    onConfirm: async () => {
      await closeCurrentModal()
      onConfirm()
    },
    onCancel: closeCurrentModal,
    alertMode: true,
  })

export const showReloadContactsDialog = async (
  onConfirm: () => Promise<unknown>,
) =>
  showConfirmModal({
    title: t('settings.sections.contacts.reloadContacts'),
    description: t('confirmDialog.reloadContacts.description'),
    confirmOption: t('common.reload'),
    cancelOption: t('common.cancel'),
    onConfirm: async () => {
      await closeCurrentModal()
      onConfirm()
    },
    onCancel: closeCurrentModal,
  })

export const showDeleteWorkspaceConfirmModal = (workspaceId: string) =>
  showConfirmModal({
    title: t('confirmDialog.deleteWorkspace.title'),
    description: t('confirmDialog.deleteWorkspace.description'),
    confirmOption: t('confirmDialog.deleteWorkspace.confirmOption'),
    cancelOption: t('common.cancel'),
    showLoadingOnConfirm: true,
    onConfirm: async () => {
      await deleteWorkspace(workspaceId)
      closeCurrentModal()
      // TODO: remove halls from workspace if needed
    },
    onCancel: closeCurrentModal,
  })

export const showAddHallToWorkspaceConfirmModal = (
  workspaceId: string,
  workspaceName: string,
  roomIds: string[],
) => {
  console.log('workspaceID ', workspaceId)
  showConfirmModal({
    title: t('confirmDialog.addHallToWorkspace.title'),
    description: t('confirmDialog.addHallToWorkspace.description')(
      roomIds.length,
      workspaceName,
    ),
    confirmOption: t('common.add'),
    cancelOption: t('common.cancel'),
    onConfirm: async () => {
      await moveHallsToWorkspace(workspaceId, roomIds)
      closeCurrentModal()
    },
    onCancel: closeCurrentModal,
  })
}

export const showRemoveHallFromWorkspaceConfirmModal = (roomIds: string[]) => {
  showConfirmModal({
    title: t('confirmDialog.removeHallFromWorkspace.title'),
    description: t('confirmDialog.removeHallFromWorkspace.description')(
      roomIds.length,
    ),
    confirmOption: t('confirmDialog.removeHallFromWorkspace.confirmOption'),
    cancelOption: t('common.cancel'),
    onConfirm: async () => {
      await moveHallsToWorkspace(null, roomIds)
      closeCurrentModal()
    },
    onCancel: closeCurrentModal,
  })
}

export default {
  [COMPONENT_ID]: {
    Component: ConfirmationDialog,
  },
}
